import type { Currency, ReferralSettingsRecord, ReferralSettingsRewardLevelRecord } from '@navio/nsdk';
import { revalidateLogic } from '@tanstack/react-form';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, NoddiSheet, useAppForm } from 'noddi-ui-web';
import { z } from 'zod';
import { appliesToModeFromLists, type AppliesToModeValues } from '../appliesToMode';
import { DISCOUNT_KIND } from '../discountKind';
import { resolvePromotionCopy, type PromotionCopyLocale } from '../suggestedPromotionCopy';
import { invalidateBrandReferralQueries } from './invalidateBrandReferralQueries';
import { ReferralDiscountFormFields } from './ReferralDiscountFormFields';
import { templateToReferralDiscountPayload, toReferralDiscountCreate } from './referralDiscountPayload';
import { mapRewardLevelsForPatch } from './rewardLevelPayload';

const FORM_ID = 'edit-reward-level-form';

const validationSchema = z
  .object({
    minNumResponses: z.number().nullable(),
    discountName: z.string(),
    discountDescription: z.string(),
    discountKind: z.enum([DISCOUNT_KIND.amount, DISCOUNT_KIND.percentage]),
    discountAmount: z.number().nullable(),
    discountPercentage: z.number().nullable(),
    salesItemIds: z.array(z.number()),
    salesItemTagIds: z.array(z.number()),
    appliesToMode: z.object({
      appliesToAllProducts: z.boolean(),
      filterByProducts: z.boolean(),
      filterByTags: z.boolean()
    })
  })
  .superRefine((value, ctx) => {
    if (value.minNumResponses == null || value.minNumResponses < 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'Enter a number of at least 1',
        path: ['minNumResponses']
      });
    }
    if (value.discountKind === 'amount' && (value.discountAmount == null || value.discountAmount <= 0)) {
      ctx.addIssue({ code: 'custom', message: 'Enter a discount amount', path: ['discountAmount'] });
    }
    if (
      value.discountKind === 'percentage' &&
      (value.discountPercentage == null || value.discountPercentage <= 0 || value.discountPercentage > 100)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Enter a percentage between 1 and 100',
        path: ['discountPercentage']
      });
    }
  });

type FormValues = z.input<typeof validationSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  brandId: number;
  serviceOrganizationId?: number;
  settings: ReferralSettingsRecord;
  levelId: number;
  currency: Currency;
  locale: PromotionCopyLocale;
  onRemove?: () => void;
};

const defaultsFromLevel = (level: ReferralSettingsRewardLevelRecord): FormValues => {
  const firstTemplate = level.couponTemplates[0];
  const payload = firstTemplate
    ? templateToReferralDiscountPayload(firstTemplate)
    : { salesItemIds: [] as number[], salesItemTagIds: [] as number[] };
  return {
    minNumResponses: level.minNumResponses,
    discountName: firstTemplate?.namePublic ?? '',
    discountDescription: firstTemplate?.descriptionPublic ?? '',
    discountKind: firstTemplate?.discount && firstTemplate.discount.amount > 0 ? 'amount' : 'percentage',
    discountAmount: firstTemplate?.discount?.amount ?? null,
    discountPercentage: firstTemplate?.discountPercentage ?? null,
    salesItemIds: payload.salesItemIds,
    salesItemTagIds: payload.salesItemTagIds,
    appliesToMode: appliesToModeFromLists({
      productCount: payload.salesItemIds.length,
      tagCount: payload.salesItemTagIds.length
    })
  };
};

const emptyDefaults = (): FormValues => ({
  minNumResponses: 1,
  discountName: '',
  discountDescription: '',
  discountKind: 'amount',
  discountAmount: null,
  discountPercentage: null,
  salesItemIds: [],
  salesItemTagIds: [],
  appliesToMode: appliesToModeFromLists({ productCount: 0, tagCount: 0 }) satisfies AppliesToModeValues
});

export const EditRewardLevelSheet = ({
  open,
  onClose,
  brandId,
  serviceOrganizationId,
  settings,
  levelId,
  currency,
  locale,
  onRemove
}: Props) => {
  const levels = [...settings.rewardLevels].sort((a, b) => a.minNumResponses - b.minNumResponses);
  const level = levels.find((item) => item.id === levelId);
  const invalidateInput = serviceOrganizationId != null ? { brandId, serviceOrganizationId } : { brandId };

  const { mutateAsync: patchSettings } = noddiAsync.usePatch({
    type: URLKeys.patchBrandReferralSettings,
    queryConfig: {
      onSuccess: async () => {
        await invalidateBrandReferralQueries(invalidateInput);
        onClose();
      }
    }
  });

  const form = useAppForm({
    defaultValues: level ? defaultsFromLevel(level) : emptyDefaults(),
    validationLogic: revalidateLogic(),
    validators: { onDynamic: validationSchema },
    onSubmit: async ({ value }) => {
      if (!level || value.minNumResponses == null) {
        return;
      }
      const { name: couponName, description: couponDescription } = resolvePromotionCopy({
        name: value.discountName,
        description: value.discountDescription,
        discountKind: value.discountKind,
        discountAmount: value.discountAmount,
        discountPercentage: value.discountPercentage,
        appliesToAllProducts: value.appliesToMode.appliesToAllProducts,
        currency,
        locale
      });
      if (!couponName) {
        return;
      }
      const remainingTemplates = level.couponTemplates.slice(1);
      await patchSettings({
        brandId,
        serviceOrganizationId,
        rewardLevels: mapRewardLevelsForPatch({
          levels,
          levelId,
          next: {
            minNumResponses: value.minNumResponses,
            discounts: [
              toReferralDiscountCreate({
                name: couponName,
                description: couponDescription,
                salesItemIds: value.salesItemIds,
                salesItemTagIds: value.salesItemTagIds,
                discount:
                  value.discountKind === 'amount' && value.discountAmount != null
                    ? { amount: value.discountAmount, currency }
                    : null,
                discountPercentage: value.discountKind === 'percentage' ? value.discountPercentage : null
              }),
              ...remainingTemplates.map((template) =>
                toReferralDiscountCreate(templateToReferralDiscountPayload(template))
              )
            ]
          }
        })
      });
    }
  });

  return (
    <form.AppForm>
      <NoddiSheet
        key={levelId}
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title='Edit reward level'
        description='Changes apply to future reward unlocks.'
        footer={
          <>
            <form.SubscribeButton formId={FORM_ID} label='Save' className='w-full' />
            {onRemove ? (
              <NoddiButton
                variant='destructiveOutline'
                className='w-full'
                onClick={() => {
                  onRemove();
                  onClose();
                }}
              >
                Remove
              </NoddiButton>
            ) : null}
          </>
        }
      >
        <form
          id={FORM_ID}
          className='flex flex-col gap-5'
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.AppField name='minNumResponses'>
            {(field) => (
              <field.Input
                label='From successful referral'
                type='number'
                min={1}
                required
                tooltip='Lower end of this reward band.'
              />
            )}
          </form.AppField>
          <ReferralDiscountFormFields
            form={form}
            currency={currency}
            locale={locale}
            beforeDiscount={<h4 className='font-semibold'>Referrer coupon</h4>}
            nameField={{
              name: 'discountName',
              label: 'Coupon name',
              tooltip: 'Shown on the coupon the referrer receives.'
            }}
            descriptionField={{
              name: 'discountDescription',
              label: 'Description',
              tooltip: 'Short explanation of this reward for the referrer.'
            }}
          />
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
